import { Accordion } from "components/Accordion/Accordion";
import { Button } from "components/Button/Button";
import { Form } from "components/Form/components/Form/Form";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { getCanvasReadonlyAffordance } from "components/ObjectTemplateCanvas/components/components";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import RecordLabel from "components/RecordLabel";
import { FormField } from "config/config";
import customObjectConfig from "config/resources/custom_object";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import { useDefaultObjectTemplate } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/utils";
import { HiddenActionsComponent } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/HiddenActionsComponent";
import { valueAndSchema } from "containers/Resources/screens/ResourcesSingle/constants";
import { getDeletedBadges } from "containers/Resources/screens/ResourcesSingle/utils";
import { Custom_Object } from "graphql/generated/graphql";
import { cloneDeep } from "lodash";
import { useMemo, useRef } from "react";
import { Form as FinalForm } from "react-final-form";
import { useHistory } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import customObjectStyles from "./CustomObjectNode.module.scss";
import styles from "./ObjectTemplateCard.module.scss";

export interface CustomObjectCardProps {
    data?: Custom_Object;
    fetchCustomObjectList: () => Promise<any>;
    formStructure?: FormField[];
    setLoading: (isLoading: boolean) => void;
    expandedId: string | null;
    setExpandedId: (id: string | null) => void;
    isFocused?: boolean;
}

/**
 * CustomObjectChildCard component that renders a read-only view of custom objects
 * within the canvas system with accordion-based expandable interface.
 *
 * This component handles:
 * - Display of custom object data in an accordion format
 * - Integration with object template schemas for structured data display
 * - Expansion state management coordinated with parent canvas
 * - Resource badges and status indicators
 * - Action buttons for navigation and resource management
 * - Form field filtering for canvas-specific display
 *
 * The component provides a compact view of custom objects that can be expanded
 * to show detailed information while maintaining visual consistency with the
 * broader canvas interface.
 *
 * @param data - The custom object data to display
 * @param fetchCustomObjectList - Function to fetch updated custom object list
 * @param formStructure - Structure of the form fields
 * @param setLoading - Function to set loading state
 * @param expandedId - ID of the currently expanded custom object
 * @param setExpandedId - Function to set the expanded custom object ID
 * @returns JSX.Element - The rendered custom object child card
 */
export const CustomObjectChildCard = ({
    data,
    fetchCustomObjectList,
    formStructure,
    expandedId,
    setExpandedId,
    isFocused,
}: CustomObjectCardProps) => {
    const history = useHistory();

    const customObjectResourceName = customObjectConfig.resourceName;

    const isExpanded = data?.id === expandedId;

    const formRef = useRef<any>(null);

    // Modify the custom object config to add after-action handlers for certain actions
    const changedCustomObjectConfig = useMemo(() => {
        const customObjectConfigCopy = cloneDeep(customObjectConfig);
        const hiddenActions = customObjectConfigCopy.actions.hidden.map((action) => {
            if (action.key === "move_to_location" || action.key === "deletePermanently") {
                action.hasAfterAction = true;
            }
            return action;
        });
        customObjectConfigCopy.actions.hidden = hiddenActions;
        return customObjectConfigCopy;
    }, []);

    // Filter form structure to only show fields that should be displayed in canvas
    const filteredFormStructure = useMemo(
        () => formStructure?.filter((field) => field.isShownInCanvas !== false),
        [formStructure]
    );

    // Get the default object template schema for structured data display
    const { defaultObjectTemplateSchema } = useDefaultObjectTemplate(data?.object_template_id);

    // Prepare initial values including schema information for structured data
    const initialValues = useMemo(() => {
        const valueAndSchemaFieldValues: SchematizedValues = {
            values: data?.value,
            schema: defaultObjectTemplateSchema,
        };

        return { ...(data ?? {}), [valueAndSchema]: valueAndSchemaFieldValues };
    }, [data, defaultObjectTemplateSchema]);

    // Configure accordion props for expansion behavior
    const accordionProps = {
        className: `customObjectCardAccordion ${
            isExpanded ? "isExpanded" : "accordion-hideChildren"
        }`,
        isExpanded: isExpanded,
        onExpandedChange: () => setExpandedId(data?.id ?? null),
        expandOnIconClick: false,
    };

    /**
     * Header wrapper component showing provisioning state and object name
     * @returns JSX.Element - The header wrapper with state dot and name
     */
    const nameWrapper = (
        <div className={customObjectStyles.headerWrapper}>
            {data ? (
                <ProvisioningStateDot
                    id={data.id}
                    wrapperClassName={customObjectStyles.provisioningStateDot}
                />
            ) : null}
            <div className={customObjectStyles.customObjectNameFieldContainer}>
                <RecordLabel
                    name={data?.name}
                    // Standard (non-overlapping) truncation tooltip on the canvas.
                    // The in-place "overlap" tooltip is rendered in a portal OUTSIDE
                    // the react-flow zoom transform, while the title lives inside it,
                    // so a fixed-size/fixed-offset overlay can never sit centered over
                    // the scaled title — it reads as a differently-sized, too-low
                    // label (operator-task-fe-canvas-title-hover-centering-font-size).
                    hasOverlappingTooltip={false}
                    // Keep the caret pointing at nodes near the canvas' right/left
                    // border (operator-task-fe-canvas-title-hover-centering-font-size).
                    edgeAwarePlacement
                />
            </div>
        </div>
    );

    /**
     * Action buttons wrapper component
     * @param pristine - Whether the form is pristine (has no changes)
     * @returns JSX.Element - The action buttons container
     */
    const actionsWrapper = (pristine) => (
        <div className={customObjectStyles.actionButtons}>
            {/* Open in new tab button */}
            <Button
                disabled={!data?.id}
                icon={IconCollection.openInNew}
                variation="icon"
                onClick={() => {
                    history.push(data?.id ? getRelativeUrl(customObjectResourceName, data.id) : "");
                }}
            />
            {/* Hidden actions component for additional resource actions */}
            <HiddenActionsComponent
                pristine={pristine}
                resourceData={data}
                resourceName={customObjectResourceName}
                resourceConfig={changedCustomObjectConfig}
                afterAction={fetchCustomObjectList}
                fetchResourceData={fetchCustomObjectList}
                hiddenActionList={["duplicate"]}
            />
        </div>
    );

    return (
        <div
            className={`${styles.formWrapper} ${customObjectStyles.customObjectForm} ${
                isExpanded ? "" : customObjectStyles.collapsedChildCard
            } ${isFocused ? customObjectStyles.focused : ""}`}
        >
            <FinalForm onSubmit={() => {}} initialValues={initialValues} destroyOnUnregister>
                {({ handleSubmit, pristine, values, form, hasValidationErrors }) => {
                    formRef.current = form;

                    return (
                        <Form>
                            <Accordion
                                title={
                                    isExpanded ? null : (
                                        /* Collapsed view: show compact header with name */
                                        <div className={customObjectStyles.customObjectHeader}>
                                            {nameWrapper}
                                        </div>
                                    )
                                }
                                actionButton={actionsWrapper(pristine)}
                                {...accordionProps}
                            >
                                {/* Expanded view: show full interface */}
                                <div
                                    className={`${styles.actionButtonContainer} ${customObjectStyles.customObjectActionButtonContainer}`}
                                >
                                    <div className={customObjectStyles.customObjectBadgesContainer}>
                                        {getCanvasReadonlyAffordance({
                                            resourceData: data,
                                            resourceConfig: customObjectConfig,
                                        })}
                                        {getDeletedBadges({ resourceData: data, size: "medium" })}
                                    </div>
                                    {actionsWrapper(pristine)}
                                </div>
                                {nameWrapper}
                                <div className={customObjectStyles.customObjectFormFieldsWrapper}>
                                    <ResourceEditFields
                                        formStructure={filteredFormStructure}
                                        isReadOnly={true}
                                        isDisabled={true}
                                        resourceTypeName={customObjectResourceName}
                                        recordId={data?.id || ""}
                                        writeOnlyFieldsWithData={{}}
                                        // Render the card's value schema-form
                                        // read-only. The card is display-only, so
                                        // every field should be read-only — and
                                        // critically, object-template reference
                                        // attributes must NOT mount the editable
                                        // Autocomplete, whose useAsyncSelect resolves
                                        // its value with a per-record
                                        // `custom_object_list` (limit 1) query on
                                        // mount. On every canvas data-rebuild all
                                        // cards remount, firing one query per
                                        // reference field (~80 in a 50ms burst on the
                                        // Agent_ops object canvas: the residual "fan
                                        // keeps whirring"). isReadOnly routes
                                        // references to SchemaFormReadonlyReference
                                        // (plain linked text, no query) — which also
                                        // satisfies ui-design-principle-affordance-
                                        // interactivity (a disabled picker reads as a
                                        // clickable button). Mechanism:
                                        // additionalFieldProps spreads into the
                                        // schemaForm field's fieldProps, and
                                        // SchemaFormFormComponent propagates
                                        // fieldProps.isReadOnly into inputProps.isReadOnly
                                        // — the signal SchemaForm/fragments actually read.
                                        additionalFieldProps={{
                                            renderIconOnly: true,
                                            isReadOnly: true,
                                        }}
                                    />
                                </div>
                            </Accordion>
                        </Form>
                    );
                }}
            </FinalForm>
        </div>
    );
};
